import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, shopApi, setOnUnauthorized } from '../api/client';
import { SecureStorageService } from '../services/SecureStorageService';
import { NotificationService } from '../services/NotificationService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setOnUnauthorized(() => {
      setUser(null);
    });
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const stored = await SecureStorageService.getUserInfo();
      const token = await SecureStorageService.getAccessToken();
      if (stored && token) {
        let userData = stored;
        if (userData.isShopOwner && !userData.shopId) {
          try {
            const shopsRes = await shopApi.myShops();
            const shops = shopsRes.data.data;
            if (shops && shops.length > 0) {
              userData.shopId = shops[0].id;
              userData.shopSlug = shops[0].slug;
              userData.shopLogo = shops[0].logo;
              await SecureStorageService.setUserInfo(userData);
            }
          } catch (err) {
            console.error("Failed to fetch shop details during init:", err);
          }
        }
        setUser(userData);
        // Register device token on app startup if already logged in
        NotificationService.registerDeviceToken();
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (mobile, password) => {
    const res = await authApi.login({ mobile, password });
    const data = res.data.data;
    await SecureStorageService.setAccessToken(data.accessToken);
    await SecureStorageService.setRefreshToken(data.refreshToken);
    if (data.isShopOwner) {
      try {
        const shopsRes = await shopApi.myShops();
        const shops = shopsRes.data.data;
        if (shops && shops.length > 0) {
          data.shopId = shops[0].id;
          data.shopSlug = shops[0].slug;
          data.shopLogo = shops[0].logo;
        }
      } catch (err) {
        console.error("Failed to fetch shop details on login:", err);
      }
    }
    await SecureStorageService.setUserInfo(data);
    setUser(data);
    // Register device token on login
    NotificationService.registerDeviceToken();
    return data;
  }, []);

  const register = useCallback(async (name, mobile, password, email, slug) => {
    const res = await authApi.register({ name, mobile, password, email, slug });
    const data = res.data.data;
    await SecureStorageService.setAccessToken(data.accessToken);
    await SecureStorageService.setRefreshToken(data.refreshToken);
    await SecureStorageService.setUserInfo(data);
    setUser(data);
    // Register device token on register
    NotificationService.registerDeviceToken();
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Unregister device first to prevent orphan notifications
    await NotificationService.unregister();
    try { await authApi.logout(); } catch {}
    await SecureStorageService.clearAll();
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      SecureStorageService.setUserInfo(updated);
      return updated;
    });
  }, []);

  const continueAsGuest = useCallback(() => {
    const guestUser = {
      isGuest: true,
      id: '00000000-0000-0000-0000-000000000000',
      name: 'Guest',
      mobile: '0000000000',
      isShopOwner: false
    };
    setUser(guestUser);
    return guestUser;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, continueAsGuest, isAuth: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
